import React, { useEffect, useState, useRef } from 'react';
import { useUI } from '../UIContext';

export default function CustomTagInput({
  tags,
  setTags,
  suggestions = [],
  style = {},
  inputStyle = {},
  onTagClick = null,
  layout = 'stacked',
  showInputByDefault = true,
  focusOnOpen = true,
  addButtonLabel = '+ Tag',
  addButtonClassName = '',
  align = null,
  inputClassName = 'tag-input',
  tagChipVariant = 'default'
}) {
  const [input, setInput] = useState('');
  const [inputOpen, setInputOpen] = useState(showInputByDefault);
  const { textStyle } = useUI();
  const inputRef = useRef(null);
  const lastAddTimeRef = useRef(0);

  const normalizeTag = (tag) => tag.trim().toLowerCase();

  const addTag = (rawInput) => {


    const existingNormalized = new Set(tags.map(normalizeTag));

    const newNormalized = new Set();
    rawInput
      .split(',')
      .map(normalizeTag)
      .forEach((tag) => {
        if (tag && !existingNormalized.has(tag)) {
          newNormalized.add(tag);
        }
      });

    if (newNormalized.size > 0) {
      setTags([...tags, ...Array.from(newNormalized)]);
    }

    setInput('');
  };

  const removeTag = (tag) => {
    setTags(tags.filter(t => normalizeTag(t) !== normalizeTag(tag)));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addTag(input);
    }
  };

  const parts = input.split(',');
  const lastTerm = parts[parts.length - 1].trim().toLowerCase();
  const priorTerms = parts.slice(0, -1).map(normalizeTag);
  const previewTags = [...priorTerms, lastTerm]
    .filter((t, i, arr) =>
      t.length > 0 &&
      arr.indexOf(t) === i && // remove duplicates within preview
      !tags.map(normalizeTag).includes(t)
    );

  const filteredSuggestions =
    lastTerm === ''
      ? suggestions.filter(s => !tags.map(normalizeTag).includes(normalizeTag(s)))
      : suggestions.filter(s =>
          s.toLowerCase().includes(lastTerm) &&
          !tags.map(normalizeTag).includes(normalizeTag(s))
        );
  const isInline = layout === 'inline';
  const isCompactInline = isInline && !showInputByDefault;
  const justify = align || (isInline ? 'flex-start' : 'center');

  useEffect(() => {
    if (focusOnOpen && inputOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [inputOpen, focusOnOpen]);

const handleSuggestionClick = (s) => {
  const currentParts = input.split(',');
  const trimmedLast = currentParts[currentParts.length - 1].trim();

  if (currentParts.length === 1 && trimmedLast.length === 0) {
    // Case 1: User clicked input but hasn't typed anything
    addTag(s);
  } else if (currentParts.length === 1 && trimmedLast.length > 0) {
    // Case 2: User is typing the first tag (e.g., "j") and clicks a suggestion
    addTag(s);
  } else {
    // Case 3: User already typed a comma (multi-tag input), continue filling
    currentParts[currentParts.length - 1] = ` ${s}`;
    setInput(currentParts.join(',').replace(/\s*,\s*$/, '') + ', ');
  }
};




  const detailTagChipStyle = {
    background: 'color-mix(in srgb, var(--text-link, #228be6) 10%, var(--input-bg) 90%)',
    color: 'var(--text-primary)',
    border: '1px solid color-mix(in srgb, var(--text-link, #228be6) 28%, transparent)',
    boxShadow: 'none',
  };

  return (
    <div style={{ ...style, display: 'flex', flexDirection: 'column', gap: '4px' }}>
      {/* Selected tags */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px', marginBottom: tags.length > 0 ? '4px' : '0', marginTop: tags.length > 0 ? '4px' : '0', justifyContent: justify }}>
        {tags.map((tag, i) => (
          <span key={i} style={{
            background: 'var(--input-bg)',
            color: 'var(--input-text, var(--text-primary))',
            padding: '0 8px',
            height: '26px',
            borderRadius: '12px',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            fontSize: '0.75rem',
            lineHeight: 1,
            boxSizing: 'border-box',
            ...(tagChipVariant === 'detail' ? detailTagChipStyle : {}),
          }}>
            <span
              onClick={onTagClick ? () => onTagClick(tag) : undefined}
              style={{ cursor: onTagClick ? 'pointer' : 'default', textDecoration: onTagClick ? 'underline' : 'none' }}
            >
              {tag}
            </span>
            <button
              onClick={(e) => {
                e.stopPropagation();
                removeTag(tag);
              }}
              style={{
                background: 'transparent',
                border: 'none',
                fontSize: '0.75rem',
                cursor: 'pointer',
                padding: 0,
                marginLeft: '2px',
                lineHeight: 1,
                color: 'var(--input-text, var(--text-primary))',
                ...(tagChipVariant === 'detail' ? { color: 'var(--text-primary)' } : {}),
              }}
            >
              ×
            </button>
          </span>
        ))}
        {!showInputByDefault && !inputOpen && (
          <button
            className={`btn-subtle ${addButtonClassName}`.trim()}
            onClick={() => setInputOpen(true)}
            style={{ fontSize: '0.75rem', padding: '4px 10px' }}
          >
            {addButtonLabel}
          </button>
        )}
        {isCompactInline && inputOpen && (
          <>
            <div style={{ flex: '0 0 170px', minWidth: '140px', maxWidth: '190px' }}>
              <input
                ref={inputRef}
                className={inputClassName}
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Add tag"
                style={{ ...inputStyle, width: '100%' }}
              />
            </div>
            <button
              className="btn-subtle"
              onClick={() => {
                setInput('');
                setInputOpen(false);
              }}
              style={{ fontSize: '0.75rem', padding: '4px 8px' }}
            >
              Done
            </button>
          </>
        )}
      </div>

      {(showInputByDefault || inputOpen) && isInline && !isCompactInline ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginTop: '2px' }}>
          <div style={{ flex: '0 0 220px', minWidth: '180px', maxWidth: '240px' }}>
            <input
              ref={inputRef}
              className={inputClassName}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Add tag"
              style={{ ...inputStyle, width: '100%' }}
            />
          </div>
          {!showInputByDefault && (
            <button
              className="btn-subtle"
              onClick={() => {
                setInput('');
                setInputOpen(false);
              }}
              style={{ fontSize: '0.75rem', padding: '4px 8px' }}
            >
              Done
            </button>
          )}
          {input.trim().length > 0 && filteredSuggestions.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', justifyContent: justify === 'center' ? 'center' : 'flex-start', flex: '1 1 140px' }}>
              {filteredSuggestions.slice(0, 8).map((s, i) => (
                <div
                  key={i}
                  onMouseDown={() => handleSuggestionClick(s)}
                  style={{
                    ...textStyle,
                    cursor: 'pointer',
                    background: 'var(--button-bg)',
                    color: 'var(--button-text)',
                    padding: '4px 10px',
                    borderRadius: '12px',
                    fontSize: '0.75rem',
                    display: 'inline-block',
                  }}
                >
                  {s}
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (showInputByDefault || inputOpen) && !isInline ? (
        <>
          {/* Input */}
          <div style={{ width: '100%', minWidth: 0, maxWidth: '100%' }}>
            <input
              ref={inputRef}
              className={inputClassName}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Add tags (click or comma-separated)"
              style={{ ...inputStyle, width: '100%' }}
            />
          </div>
          {!showInputByDefault && (
            <div style={{ display: 'flex', justifyContent: justify }}>
              <button
                className="btn-subtle"
                onClick={() => {
                  setInput('');
                  setInputOpen(false);
                }}
                style={{ fontSize: '0.75rem', padding: '4px 8px' }}
              >
                Done
              </button>
            </div>
          )}

          {/* Preview */}
          {previewTags.length > 0 && (
            <div style={{ fontSize: '0.75rem', color: 'gray', marginTop: '2px', justifyContent: 'center' }}>
              Enter to add: {previewTags.join(', ')}
            </div>
          )}

          {/* Suggestions */}
          {input.trim().length > 0 && filteredSuggestions.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '6px', justifyContent: 'center' }}>
              {filteredSuggestions.slice(0, 8).map((s, i) => (
                <div
                  key={i}
                  onMouseDown={() => handleSuggestionClick(s)}
                  style={{
                    ...textStyle,
                    cursor: 'pointer',
                    background: 'var(--button-bg)',
                    color: 'var(--button-text)',
                    padding: '4px 10px',
                    borderRadius: '12px',
                    fontSize: '0.75rem',
                    display: 'inline-block',
                  }}
                >
                  {s}
                </div>
              ))}
            </div>
          )}
        </>
      ) : null}
      {isCompactInline && inputOpen && input.trim().length > 0 && filteredSuggestions.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '4px', justifyContent: justify }}>
          {filteredSuggestions.slice(0, 8).map((s, i) => (
            <div
              key={i}
              onMouseDown={() => handleSuggestionClick(s)}
              style={{
                ...textStyle,
                cursor: 'pointer',
                background: 'var(--button-bg)',
                color: 'var(--button-text)',
                padding: '4px 10px',
                borderRadius: '12px',
                fontSize: '0.75rem',
                display: 'inline-block',
              }}
            >
              {s}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
